import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import omitBy from 'lodash/omitBy';
import mapValues from 'lodash/mapValues';
import addDays from 'date-fns/add_days';
import differenceBy from 'lodash/differenceBy';

import apiSearch from '~/api/search';
import {companyBroker} from '~/api/company';
import * as searchHelper from '~/misc/search';
import types from './types';
import {promiseAction} from '~/misc/dispatch';
import create from '~/misc/create';
import {track} from '~/misc/metrics';
import {searchProps} from '~/misc/metrics/utils';
import {loadsSearch} from '~/modules/loadsSearch/actions';
import {form2query} from '~/misc/search';

const expandDate = (original) => {
  if (!original.date || (Array.isArray(original.date) && !original.date.length)) {
    return original.date;
  }

  const list = Array.isArray(original.date) ? original.date : [original.date];
  const maxDate = Math.max(...list);

  const date = [addDays(maxDate, 1)];

  if (isEqual(original.date, date)) {
    return original.date;
  }

  return date;
};

const expandCriteria = (query) => ({
  ...query,
  dho: query.dho ? (parseFloat(query.dho) + 100) : query.dho,
  dhd: query.dhd ? (parseFloat(query.dhd) + 200) : query.dhd,
  date: expandDate(query),
});

const getRelaxedQuery = (query) => {
  let result = expandCriteria(query);
  // let result = query

  if (isEqual(result, query)) {
    return null;
  }

  return result;
};

const similarsLimit = 50;

export const relaxAndSearch = (relaxed) => (dispatch, getState) => {
  if (!relaxed) {
    dispatch(create(types.SIMILAR_LOADS_CRITERIA, null));
    return Promise.resolve();
  }

  dispatch(create(types.SIMILAR_LOADS_CRITERIA, relaxed));

  const newQuery = relaxed;

  const mainResults = getState().loadsSearch;
  if (mainResults.total > 50) {
    return Promise.resolve();
  }
  
  return apiSearch(getState, searchHelper.toServerLegacy({
    ...newQuery,
    limit: similarsLimit + get(mainResults, 'results.shipments.length', 0),
  }), false)
    .then((result) => {
      if (!result) {
        return Promise.resolve();
      }

      if (!isEqual(getState().similarLoads.query, newQuery)) {
        return Promise.resolve(null);
      }

      const uniq = differenceBy(result.shipments, get(getState(), 'loadsSearch.results.shipments', []), 'id');
      return {
        total_shipments: result.total_shipments,
        shipments: uniq.slice(0, similarsLimit),
      };
    })
    .catch((reason) => (
      isEqual(getState().similarLoads.query, newQuery)
        ? Promise.reject(reason)
        : Promise.resolve(null))
    );
};

export const similarLoadsSort = (query) => (dispatch, getState) =>
  promiseAction(dispatch, getState, types.SIMILAR_LOADS_SORTED, dispatch(relaxAndSearch(query)));

export const hookSimilar = (doMain, options = {}) => (...args) => (dispatch, getState) =>
  promiseAction(dispatch, getState, types.SIMILAR_LOADS_FOUND, dispatch(doMain(...args)).then(() => {
    const state = getState();
    const query = state.loadsSearch.query;
    // why do excute this step ? 
    // 03/24/2022 delete below code because request list result api twice and second one request did not fetch anything.
    // request date params pass though the furture day(tommorrow from now on) to backend and response data shows nothing.
    // const relaxed = getRelaxedQuery(query);

    return dispatch(relaxAndSearch(query, getState));
  }))
    .then((res) => {
      if (!options.allowTracking) {
        return res;
      }
      const state = getState();
      let query = mapValues(omitBy(state.loadsSearch.query, isNil), v => isArray(v) ? v.join(',') : v);
      if (query.from_name === '') {
        query.from_name = 'Anywhere';
      }
      if (query.to_name === '') {
        query.to_name = 'Anywhere';
      }

      track('loadsearch_results', {
        ...searchProps(state),
        ...query,
        query,
      });

      return res;
    });

const refresh = (getState, dispatch) => {
  const arr = getState().similarLoads.results.shipments;
  const total = getState().similarLoads.total;
  if (total > 200 && !arr.length) {
    const formData = getState().loadsSearch.query;
    dispatch(loadsSearch(form2query(formData)));
  }
};
export const similarHideTheCompany = (data) => (dispatch, getState) => {
  const payload = {data, idType: 'id'};
  dispatch(create(types.SIMILAR_HIDE_THE_LOADS, payload));
  setTimeout(() => {
    refresh(getState, dispatch);
  }, 0);
};

// broker-save
export const similarCollectionTheCompany = (data) => (dispatch, getState) => {
  const payload = data;
  dispatch(create(types.COLLECTION_THE_COMPANY, payload));
  companyBroker(getState, {id: data.id, company: data.company, operate: 'save'})
    .then(response => {
      if (response.code === 200) {
        dispatch(create(types.COLLECTION_THE_COMPANY, payload));
      } else if (response.code === 500) {
        dispatch(create(types.NO_COLLECTION_COMPANY, payload));
      }
    }, () => {
      dispatch(create(types.NO_COLLECTION_COMPANY, payload));
    });
};
// broker-no-save
export const similarNoCollectionCompsny = (data) => (dispatch, getState) => {
  const payload = data;
  dispatch(create(types.NO_COLLECTION_COMPANY, payload));
  companyBroker(getState, {id: data.id, company: data.company, operate: 'remove'})
    .then(response => {
      if (response.code === 200) {
        dispatch(create(types.NO_COLLECTION_COMPANY, payload));
      } else if (response.code === 500) {
        dispatch(create(types.COLLECTION_THE_COMPANY, payload));
      }
    }, () => {
      dispatch(create(types.COLLECTION_THE_COMPANY, payload));
    });
};

// broker-hide
export const similarHideBroker = (data) => (dispatch, getState) => {
  companyBroker(getState, {id: data.id, company: data.company, operate: 'hide'})
    .then(() => {
      const payload = {data, idType: 'broker_id'};
      dispatch(create(types.SIMILAR_HIDE_THE_COMPANY, payload));
      setTimeout(() => {
        refresh(getState, dispatch);
      }, 0);
    });
};

// checked
export const similarLoadCheck = (id) => create(types.SIMILAR_LOADS_CHECKED, {id});

// checkedAll
export const similarLoadCheckedAll = () => (dispatch) => {
  dispatch({type: types.SIMILAR_LOADS_CHECKED_ALL});
};

// clear
export const clearSimilarChecked = () => (dispatch) => {
  dispatch({type: types.CLEAR_SIMILAR_CHECKED});
};

export const updateSimilarChecked = () => (dispatch) => {
  dispatch({type: types.UPDATE_SIMILAR_CHECKED});
};



// WEBPACK FOOTER //
// ./src/modules/similarLoads/actions.js